import { Box, Grid } from '@chakra-ui/react'
import { useCallback, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { EmptyState } from '@mangovoice/mango-ui'

import type { HotelsApi } from '../../api/hotelsApi'
import { useHotelsI18n } from '../../i18n'
import type { EnrichedRoom, HospitalityPermissions } from '../../types/rooms'
import type { WakeupNotificationTone } from '../../utils/wakeupDashboard'
import { RoomDetailModal } from './RoomDetailModal'
import { RoomStatusTile } from './RoomStatusTile'

interface RoomStatusGridProps {
  rooms: EnrichedRoom[]
  api: HotelsApi
  pbxId: number
  pbxUuid: string
  pbxTimeZone: string
  toast: ToastLike
  permissions: HospitalityPermissions
  wakeupTonesByRoomId?: Map<number, WakeupNotificationTone>
}

export const RoomStatusGrid = ({
  rooms,
  api,
  pbxId,
  pbxUuid,
  pbxTimeZone,
  toast,
  permissions,
  wakeupTonesByRoomId,
}: RoomStatusGridProps) => {
  const { text } = useHotelsI18n()
  const [selectedRoom, setSelectedRoom] = useState<EnrichedRoom | null>(null)

  const handleTileClick = useCallback((room: EnrichedRoom) => {
    setSelectedRoom(room)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedRoom(null)
  }, [])

  if (rooms.length === 0) {
    return (
      <EmptyState
        title={text('hotels.dashboard.noRoomsTitle', 'No rooms found')}
        description={text(
          'hotels.dashboard.noRoomsDescription',
          'No hospitality rooms are configured for this PBX.',
        )}
      />
    )
  }

  return (
    <>
      <Box p={1}>
        <Grid
          data-testid="room-status-grid"
          templateColumns="repeat(auto-fill, minmax(190px, 1fr))"
          gap={3}
        >
          {rooms.map((room) => (
            <RoomStatusTile
              key={room.uuid ?? room.id}
              room={room}
              onClick={handleTileClick}
              api={api}
              pbxId={pbxId}
              pbxUuid={pbxUuid}
              pbxTimeZone={pbxTimeZone}
              toast={toast}
              permissions={permissions}
              wakeupTone={wakeupTonesByRoomId?.get(room.id) ?? null}
            />
          ))}
        </Grid>
      </Box>

      {selectedRoom && (
        <RoomDetailModal
          room={selectedRoom}
          open={!!selectedRoom}
          onClose={handleCloseModal}
          api={api}
          pbxId={pbxId}
          pbxUuid={pbxUuid}
          pbxTimeZone={pbxTimeZone}
          toast={toast}
          permissions={permissions}
        />
      )}
    </>
  )
}
